import React, { useEffect, useState } from "react";
import { withRouter, Redirect } from "react-router-dom";
import LayoutComp from "../../components/Layout/LayoutComp";
import MessageComp from "../../components/SharedComp/MessageComp";
import ActionMenu from "../../components/SharedComp/ActionMenu";
import DesignModal from "../../components/SharedComp/DesignModal";
import ConfirmDialog, { DEACTIVATE_COPY } from "../../components/SharedComp/ConfirmDialog";
import ZoneConfiguration from "./ZoneConfiguration";
import NetworkConfiguration from "./NetworkConfiguration";
import AddOREditZone from "../ZoneOverview/AddOREditZone";
import ZoneContext from "../../Utils/Context/ZoneContext";
import zoneServices from "../../services/zoneServices";
import saveZone from "../ZoneOverview/saveZone";
import { classify, DESIGN_TEXT, OUTCOME } from "../../Utils/e5Messages";
import { ErrorHandler } from "../../Utils/Constants.";

/**
//    * @component  Zone Network Configuration Component
//    * @description  Zone Network configuration details component to show both zone and network configuration details.
//    * E5: header ⋯ → Edit Zone / Deactivate Zone.
//    * @returns Zone Network configuration  UI
//    */
function ZoneNetworkConfiguration(props) {
  const [showMsgComp, setShowMsgComp] = useState(false); // Manage flag for Massage
  const [msgContent, setMsgContent] = useState({}); //Manage content of Massage
  const [exportDisable, setExportDisabled] = useState(false);

  /**
   * set zone details (kept in state so an Edit Zone save updates the page)
   */
  const [zone, setZone] = useState(
    props.location.state && props.location.state.zoneData
      ? props.location.state.zoneData
      : null
  );

  const [headerData] = useState(() => {
    const data = JSON.parse(localStorage.getItem("userData"));
    return {
      Authorization: `Bearer ${
        data && data.authenticationToken ? data.authenticationToken.authToken : ""
      }`,
    };
  });

  // Edit Zone modal
  const [editOpen, setEditOpen] = useState(false);
  const [timeZoneList, setTimeZoneList] = useState([]);
  const [loaderSave, setLoaderSave] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogLoader, setDialogLoader] = useState(false);

  // Deactivate Zone confirmation
  const [deactivateOpen, setDeactivateOpen] = useState(false);
  const [deactivateBusy, setDeactivateBusy] = useState(false);

  useEffect(() => {
    if (!zone) return;
    zoneServices
      .getTimeZoneList({ DropdownKey: "Timezone" }, headerData)
      .then((response) => {
        if (response && response.data && response.data.httpStatusCode === 200) {
          setTimeZoneList(response.data.apiResponse);
        }
      })
      .catch((e) => ErrorHandler(e));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getCountOfNetwork = (count) => {
    setExportDisabled(count > 0 ? false : true);
  };

  const showToast = (msg, type) => {
    setShowMsgComp(true);
    setMsgContent({ msg, type });
  };

  const closeEdit = () => {
    setEditOpen(false);
    setDialogOpen(false);
  };

  /**
   * Edit Zone save: same rules and feedback as Zone Overview; on success the page shows the new values
   */
  const handleSubmit = async (values, id) => {
    if (dialogOpen === true) {
      setDialogLoader(true);
    } else {
      setLoaderSave(true);
    }
    const { feedback, response } = await saveZone(values, id, headerData);
    setLoaderSave(false);
    setDialogLoader(false);
    setDialogOpen(false);

    if (feedback.ok) {
      const tz = timeZoneList.find((t) => String(t.id) === String(values.timeZoneDetails));
      const updated = {
        ...zone,
        zoneName: values.zoneName,
        scheduleFileBasePath: values.scheduleFileBasePath,
        verificationFileBasePath: values.verificationFileBasePath,
        timeZoneDetails: tz ? tz : zone.timeZoneDetails,
      };
      setZone(updated);
      // keep the new values if the page is revisited through history
      props.history.replace({ ...props.location, state: { zoneData: updated } });
      closeEdit();
      showToast(DESIGN_TEXT.zoneUpdated, "success");
    } else if (feedback.toast) {
      ErrorHandler(response);
      showToast(feedback.toast, "error");
    }
    return feedback;
  };

  /**
   * Deactivate Zone: success (or already gone) returns to Zone Overview with the toast;
   * anything else keeps the page and shows the error toast (design)
   */
  const confirmDeactivate = async () => {
    setDeactivateBusy(true);
    const response = await zoneServices.DeactivateZone(zone.id, headerData);
    const result = classify(response);
    setDeactivateBusy(false);
    setDeactivateOpen(false);

    if (result.outcome === OUTCOME.SUCCESS) {
      props.history.push({
        pathname: "/home",
        state: { toast: { msg: DESIGN_TEXT.zoneDeactivated, type: "success" } },
      });
    } else if (
      result.outcome === OUTCOME.ALREADY_IN_STATE ||
      result.outcome === OUTCOME.NOT_FOUND
    ) {
      props.history.push({
        pathname: "/home",
        state: { toast: { msg: result.text, type: "error" } },
      });
    } else {
      ErrorHandler(response);
      showToast(result.outcome === OUTCOME.AUTH ? result.text : DESIGN_TEXT.generic, "error");
    }
  };

  // opened without a zone (page refresh or typed URL): nothing to show, go back to the list
  if (!zone) {
    return <Redirect to="/home" />;
  }

  return (
    <LayoutComp>
      <>
        <MessageComp msg={msgContent} id="login-success" show={showMsgComp} />
        <div>
          <ZoneConfiguration
            zoneData={zone}
            setShowMsgComp={setShowMsgComp}
            setMsgContent={setMsgContent}
            exportDisable={exportDisable}
            actions={
              <ActionMenu
                ariaLabel="Zone actions"
                sx={{ width: 28, height: 28, borderRadius: "6px" }}
                items={[
                  { label: "Edit Zone", onClick: () => setEditOpen(true) },
                  {
                    label: "Deactivate Zone",
                    destructive: true,
                    onClick: () => setDeactivateOpen(true),
                  },
                ]}
              />
            }
          />

          <NetworkConfiguration
            loadData={false}
            id={zone.id}
            zone={zone}
            setShowMsgComp={setShowMsgComp}
            setMsgContent={setMsgContent}
            getCountOfNetwork={getCountOfNetwork}
          />
        </div>

        <ZoneContext.Provider
          value={{
            handleSubmit,
            loaderSave,
            dialogOpen,
            dialogLoader,
            hightLightFields: {},
            onClickClose: () => setDialogOpen(true),
            confirmModalClose: () => setDialogOpen(false),
          }}
        >
          <DesignModal open={editOpen} onClose={() => !loaderSave && closeEdit()} labelledBy="zone-form-title">
            {editOpen && (
              <AddOREditZone
                zoneDetails={zone}
                timeZoneList={timeZoneList}
                onModalClose={closeEdit}
                classesTwo={{ dialog: "" }}
                classesThree={{ dialog: "" }}
              />
            )}
          </DesignModal>
        </ZoneContext.Provider>

        <ConfirmDialog
          open={deactivateOpen}
          title={DEACTIVATE_COPY.zone.title}
          label={DEACTIVATE_COPY.zone.label(zone)}
          bullets={DEACTIVATE_COPY.zone.bullets}
          busy={deactivateBusy}
          onConfirm={confirmDeactivate}
          onCancel={() => setDeactivateOpen(false)}
        />
      </>
    </LayoutComp>
  );
}

ZoneNetworkConfiguration.propTypes = {};

export default withRouter(ZoneNetworkConfiguration);
